'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2RouteContext } from '@/lib/pr-v2/route-utils';
import type { Collaborators, ReviewerSelection, TeamReviewerSelection } from '@/types/github';
import { UserPlus, Users } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import type { Command } from '../types';
import { COMMAND_GROUPS } from '../types';
import { useRegisterCommands } from '../use-commands';
import { prV2Command, prV2ModeRow } from './pr-v2-command-group';
import { buildTeamPickerModel, cannotRemoveReviewerMessage, canRemoveReviewer, filterAvailableReviewers } from '@/components/reviewers/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCollaborators } from '@/data/collaborators/use-collaborators';
import { useOrgTeams } from '@/data/teams/use-org-teams';
import { usePrV2ReviewerActions } from '@/data/v2/pr/use-pr-v2-reviewer-actions';
import { usePrV2Reviews } from '@/data/v2/pr/use-pr-v2-reviews';
import { usePrV2Summary } from '@/data/v2/pr/use-pr-v2-summary';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { buildPrV2Reviewers } from '@/lib/pr-v2/reviewers';
import { parsePrV2Route } from '@/lib/pr-v2/route-utils';
import { usePrStore } from '@/lib/stores/pr-store';
import { isBotLogin } from '@/utils/github/bot-detection';
type OrderingSnapshot = {
  reviewerLogins: Set<string>;
  requestedTeamSlugs: Set<string>;
};
type ReviewerBatchInput = {
  toAdd: ReviewerSelection[];
  toRemove: ReviewerSelection[];
  teamsToAdd: TeamReviewerSelection[];
  teamsToRemove: TeamReviewerSelection[];
};
function reviewerAvatar(login: string, avatarUrl: string | null | undefined) {
  return <Avatar className="size-4" aria-hidden>
      <AvatarImage src={avatarUrl ?? ''} alt="" />
      <AvatarFallback>{login.slice(0, 2).toUpperCase() || 'NA'}</AvatarFallback>
    </Avatar>;
}
const TEAM_ICON = <span className="flex size-4 items-center justify-center rounded-full bg-zinc-800">
    <Users className="size-3 text-zinc-400" />
  </span>;
function sortCollaboratorsForPalette(collaborators: Collaborators, reviewerLogins: Set<string>): Collaborators {
  return [...collaborators].sort((a, b) => {
    const aIsReviewer = reviewerLogins.has(a.login.toLowerCase());
    const bIsReviewer = reviewerLogins.has(b.login.toLowerCase());
    if (aIsReviewer !== bIsReviewer) {
      return aIsReviewer ? -1 : 1;
    }
    return a.login.localeCompare(b.login);
  });
}
function sortTeamsForPalette(teams: TeamReviewerSelection[], requestedTeamSlugs: Set<string>): TeamReviewerSelection[] {
  return [...teams].sort((a, b) => {
    const aRequested = requestedTeamSlugs.has(a.slug);
    const bRequested = requestedTeamSlugs.has(b.slug);
    if (aRequested !== bRequested) {
      return aRequested ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });
}
type ReviewerCommandsProps = {
  isPaletteOpen?: boolean;
  isActive?: boolean;
};
export const PrV2AddReviewerCommands = memo(t0 => {
  const $ = _c(7);
  const {
    isPaletteOpen: t1,
    isActive: t2
  } = t0;
  const isPaletteOpen = t1 === undefined ? false : t1;
  const isActive = t2 === undefined ? false : t2;
  const t3 = usePathname();
  let t4;
  if ($[0] !== t3) {
    t4 = parsePrV2Route(t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const route = t4;
  if (!route) {
    return null;
  }
  const t5 = `${route.owner}/${route.repo}#${route.number}`;
  let t6;
  if ($[2] !== isActive || $[3] !== isPaletteOpen || $[4] !== route || $[5] !== t5) {
    t6 = <PullRequestReviewerCommands key={t5} {...route} isPaletteOpen={isPaletteOpen} isActive={isActive} />;
    $[2] = isActive;
    $[3] = isPaletteOpen;
    $[4] = route;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
});
function PullRequestReviewerCommands({
  owner,
  repo,
  number,
  isPaletteOpen = false,
  isActive = false
}: PrV2RouteContext & ReviewerCommandsProps) {
  // Only the member PR page fills the PR store, so other viewers get no reviewer commands.
  const isMember = usePrStore(store => store.isInitialized && store.owner === owner && store.repo === repo && store.number === number);
  const isListed = isMember && isPaletteOpen;
  const summaryQuery = usePrV2Summary(owner, repo, number);
  const collaboratorsQuery = useCollaborators(owner, repo, {
    enabled: isListed,
    retry: false
  });
  const orgTeamsQuery = useOrgTeams(owner, {
    enabled: isMember && isActive
  });
  const reviewsQuery = usePrV2Reviews(owner, repo, number, {
    enabled: isListed
  });
  const {
    batchUpdateReviewers,
    isMutating: isUpdatingReviewers
  } = usePrV2ReviewerActions(owner, repo, number);
  const pullRequest = summaryQuery.data?.pullRequest ?? null;
  const authorLogin = pullRequest?.author.login ?? '';
  const installationId = pullRequest?.installationId ?? null;
  const requestedReviewers = pullRequest?.metadataSummary?.reviewers;
  const requestedTeams = useMemo(() => pullRequest?.metadataSummary?.requestedTeams ?? [], [pullRequest]);

  // Everyone the sidebar lists as a reviewer: the requested ones and those who already reviewed.
  const reviewersByLogin = useMemo(() => new Map(buildPrV2Reviewers(requestedReviewers ?? [], reviewsQuery.data ?? []).map(reviewer => [reviewer.login.toLowerCase(), reviewer])), [requestedReviewers, reviewsQuery.data]);
  const availableCollaborators = useMemo(() => {
    const collaborators = collaboratorsQuery.data ?? null;
    if (!collaborators) {
      return null;
    }
    return filterAvailableReviewers(collaborators, authorLogin).filter(collaborator => !isBotLogin(collaborator.login));
  }, [authorLogin, collaboratorsQuery.data]);
  const teamModel = useMemo(() => buildTeamPickerModel(requestedTeams, orgTeamsQuery.data ?? []), [orgTeamsQuery.data, requestedTeams]);
  const orderingSnapshotRef = useRef<OrderingSnapshot | null>(null);
  useEffect(() => {
    orderingSnapshotRef.current = isActive ? {
      reviewerLogins: new Set(reviewersByLogin.keys()),
      requestedTeamSlugs: new Set(teamModel.requestedTeamSlugs)
    } : null;
    // Snapshot only on activation flips; live sets below drive check marks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);
  const isToggleInFlightRef = useRef(false);
  const runReviewerToggle = useCallback(({
    input,
    onStart,
    onSuccessMessage
  }: {
    input: ReviewerBatchInput;
    onStart: () => void;
    onSuccessMessage: string;
  }) => {
    if (isToggleInFlightRef.current || isUpdatingReviewers) {
      toast.info('Still updating reviewers - try again in a second');
      return;
    }
    isToggleInFlightRef.current = true;
    onStart();
    batchUpdateReviewers(input).then(() => {
      toast.success(onSuccessMessage);
    }).catch((error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update reviewers');
    }).finally(() => {
      isToggleInFlightRef.current = false;
    });
  }, [batchUpdateReviewers, isUpdatingReviewers]);
  const toggleReviewer = useCallback((selection: ReviewerSelection, isRequested: boolean) => {
    const input = isRequested ? {
      toAdd: [],
      toRemove: [selection],
      teamsToAdd: [],
      teamsToRemove: []
    } : {
      toAdd: [selection],
      toRemove: [],
      teamsToAdd: [],
      teamsToRemove: []
    };
    runReviewerToggle({
      input,
      onStart: () => {
        trackClientEvent('pr_reviewers_updated', installationId?.toString(), {
          pr_number: number,
          repo: `${owner}/${repo}`,
          source: 'command-palette',
          reviewers_added: isRequested ? 0 : 1,
          reviewers_removed: isRequested ? 1 : 0,
          added_reviewers: isRequested ? [] : [selection.login],
          removed_reviewers: isRequested ? [selection.login] : [],
          teams_added: 0,
          teams_removed: 0,
          added_teams: [],
          removed_teams: []
        });
      },
      onSuccessMessage: isRequested ? `Removed ${selection.login} from reviewers` : `Requested review from ${selection.login}`
    });
  }, [installationId, number, owner, repo, runReviewerToggle]);
  const toggleTeam = useCallback((team: TeamReviewerSelection, isRequested: boolean) => {
    const input = isRequested ? {
      toAdd: [],
      toRemove: [],
      teamsToAdd: [],
      teamsToRemove: [team]
    } : {
      toAdd: [],
      toRemove: [],
      teamsToAdd: [team],
      teamsToRemove: []
    };
    runReviewerToggle({
      input,
      onStart: () => {
        trackClientEvent('pr_reviewers_updated', installationId?.toString(), {
          pr_number: number,
          repo: `${owner}/${repo}`,
          source: 'command-palette',
          reviewers_added: 0,
          reviewers_removed: 0,
          added_reviewers: [],
          removed_reviewers: [],
          teams_added: isRequested ? 0 : 1,
          teams_removed: isRequested ? 1 : 0,
          added_teams: isRequested ? [] : [team.slug],
          removed_teams: isRequested ? [team.slug] : []
        });
      },
      onSuccessMessage: isRequested ? `Removed ${team.name} from reviewers` : `Requested review from ${team.name}`
    });
  }, [installationId, number, owner, repo, runReviewerToggle]);
  useRegisterCommands(() => {
    const commands: Command[] = [];
    if (!isMember || !pullRequest || !installationId) {
      return commands;
    }

    // The mode shows its own loading and error rows, so the entry doesn't wait for collaborators.
    commands.push(prV2Command('pr-v2-add-reviewer', {
      label: 'Add reviewer...',
      icon: <UserPlus />,
      shortcutId: 'pr-reviewers-edit',
      action: () => false,
      enterMode: 'pr-v2-add-reviewer',
      keepOpen: true,
      keywords: ['reviewer', 'review', 'request', 'remove', 'assign', 'team']
    }));

    // Listed whenever the palette is open, so a search from the top finds people too.
    if (!isListed) {
      return commands;
    }
    if (!availableCollaborators) {
      if (collaboratorsQuery.isError) {
        commands.push(prV2ModeRow({
          id: 'pr-v2-add-reviewer-error-sentinel',
          label: 'You need push access to this repository to manage reviewers.',
          action: () => {},
          tag: 'pr-v2-add-reviewer-empty'
        }));
        return commands;
      }
      commands.push(prV2ModeRow({
        id: 'pr-v2-add-reviewer-loading-sentinel',
        label: 'Loading...',
        action: () => {},
        tag: 'pr-v2-add-reviewer-loading'
      }));
      return commands;
    }
    const ordering = orderingSnapshotRef.current ?? {
      reviewerLogins: new Set(reviewersByLogin.keys()),
      requestedTeamSlugs: teamModel.requestedTeamSlugs
    };
    const sortedCollaborators = sortCollaboratorsForPalette(availableCollaborators, ordering.reviewerLogins);
    for (const collaborator of sortedCollaborators) {
      const reviewer = reviewersByLogin.get(collaborator.login.toLowerCase());
      commands.push(prV2ModeRow({
        id: `pr-v2-add-reviewer:user:${collaborator.login}`,
        label: collaborator.login,
        icon: reviewerAvatar(collaborator.login, collaborator.avatar_url),
        checked: Boolean(reviewer),
        action: () => {
          // As in the sidebar, a review on record can't be withdrawn.
          if (reviewer && !canRemoveReviewer(reviewer)) {
            toast.error(cannotRemoveReviewerMessage(collaborator.login));
            return;
          }
          toggleReviewer({
            login: collaborator.login,
            avatar_url: collaborator.avatar_url
          }, Boolean(reviewer));
        },
        keepOpen: true,
        group: COMMAND_GROUPS.REVIEWERS.name,
        tag: 'pr-v2-add-reviewer'
      }));
    }
    const sortedTeams = sortTeamsForPalette(teamModel.sortedTeams, ordering.requestedTeamSlugs);
    for (const team of sortedTeams) {
      const isRequested = teamModel.requestedTeamSlugs.has(team.slug);
      commands.push(prV2ModeRow({
        id: `pr-v2-add-reviewer:team:${team.slug}`,
        label: team.name,
        icon: TEAM_ICON,
        keywords: ['team', team.slug],
        checked: isRequested,
        action: () => {
          toggleTeam(team, isRequested);
        },
        keepOpen: true,
        group: COMMAND_GROUPS.TEAMS.name,
        tag: 'pr-v2-add-reviewer'
      }));
    }
    if (sortedCollaborators.length === 0 && sortedTeams.length === 0) {
      commands.push(prV2ModeRow({
        id: 'pr-v2-add-reviewer-empty-sentinel',
        label: 'No reviewers available',
        action: () => {},
        tag: 'pr-v2-add-reviewer-empty'
      }));
    }
    return commands;
  }, [availableCollaborators, collaboratorsQuery.isError, installationId, isListed, isMember, pullRequest, reviewersByLogin, teamModel, toggleReviewer, toggleTeam]);
  return null;
}